import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import './SurveyTracking.css';

// Helper function to format time duration nicely
const formatTime = (milliseconds) => {
    if (!milliseconds || milliseconds === 0) return '0s';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
};

// ─── RecordingPlayer – matches sample project RecordingModal ──────────────────
const RecordingPlayer = ({ sessionId, onClose }) => {
    const containerRef = useRef(null);
    const playerRef    = useRef(null);

    const [phase,    setPhase]    = useState('loading'); // loading|ready|notFound|error
    const [errorMsg, setErrorMsg] = useState('');
    const [recording, setRecording] = useState(null);

    useEffect(() => {
        loadRecording();
        return () => { playerRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const loadRecording = async () => {
        setPhase('loading');
        setErrorMsg('');
        try {
            const token = localStorage.getItem('admin_token');
            const res = await axios.get(
                `/api/survey-tracking/admin/sessions/${sessionId}/recording`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = res.data;
            if (!data.events || data.events.length === 0) { setPhase('notFound'); return; }
            setRecording(data);
            setPhase('ready');
            // Give React time to render the player-wrapper div, then mount
            setTimeout(() => initializePlayer(data.events), 100);
        } catch (err) {
            if (err.response?.status === 404) {
                setPhase('notFound');
            } else {
                setErrorMsg(err.response?.data?.error || 'Failed to load recording');
                setPhase('error');
            }
        }
    };

    const initializePlayer = (events) => {
        if (!events || events.length === 0) return;

        // ── Step 1: Resolve timestamp field ─────────────────────────────────────
        // rrweb events use 'timestamp'; older custom events used 'ts'.
        // Only keep events with a reasonable Unix-ms timestamp (> 1 billion = post-2001).
        const withTs = events.map(ev => {
            let ts = 0;
            if (typeof ev.timestamp === 'number' && ev.timestamp > 1_000_000_000) {
                ts = ev.timestamp;
            } else if (typeof ev.ts === 'number' && ev.ts > 1_000_000_000) {
                ts = ev.ts;
            }
            return { ...ev, timestamp: ts };
        });

        // ── Step 2: Keep only real rrweb events (numeric type, valid timestamp) ─
        // Filter out old custom-format events ("recording_start", "input", etc.)
        // which have string types and would cause timestamp=0, breaking the player.
        const rrwebOnly = withTs
            .filter(ev => {
                const t = Number(ev.type);
                return !isNaN(t) && ev.timestamp > 0;
            })
            .sort((a, b) => a.timestamp - b.timestamp);

        // ── Step 3: Normalize to relative timestamps (start from 0) ─────────────
        // Prevents the "494373:35:22" display bug caused by absolute Unix ms values
        // being treated as elapsed time by rrweb-player.
        const minTs = rrwebOnly.length > 0 ? rrwebOnly[0].timestamp : 0;
        const normalizedEvents = rrwebOnly.map(ev => ({
            ...ev,
            timestamp: ev.timestamp - minTs
        }));

        // ── rrweb playback needs at least one FullSnapshot (type === 2). ────────
        const hasFullSnapshot = normalizedEvents.some(ev => Number(ev.type) === 2);
        if (!hasFullSnapshot) {
            // recording state is already set – just switch phase
            setPhase('customEvents');
            return;
        }

        if (!containerRef.current) return;

        // Clear any existing player
        if (playerRef.current) {
            playerRef.current = null;
            containerRef.current.innerHTML = '';
        }

        try {
            // Fit player to wrapper (16:9-ish) exactly like sample RecordingModal
            const w = containerRef.current.offsetWidth  || 900;
            const h = containerRef.current.offsetHeight || 500;
            const playerWidth  = Math.min(w, 1100);
            const playerHeight = Math.min(h, Math.round((playerWidth * 9) / 17));

            playerRef.current = new rrwebPlayer({
                target: containerRef.current,
                props: {
                    events: normalizedEvents,
                    width:          playerWidth,
                    height:         playerHeight,
                    autoPlay:       false,
                    showController: true,
                    speedOption:    [1, 2, 4, 8],
                    skipInactive:   false,
                    showWarning:    true,
                },
            });
        } catch (err) {
            console.error('[rrweb-player]', err);
            setErrorMsg('Failed to initialize player: ' + err.message);
            setPhase('error');
        }
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const s = Math.floor(ms / 1000);
        const m = Math.floor(s / 60);
        return `${m}:${String(s % 60).padStart(2, '0')}`;
    };

    return (
        <div className="recording-modal-overlay" onClick={onClose}>
            <div className="recording-modal-content" onClick={e => e.stopPropagation()}>

                {/* ── Close button (circular, top-right) ── */}
                <button className="recording-modal-close" onClick={onClose}>✕</button>

                {/* ── Body ── */}
                <div className="recording-modal-body">

                    {phase === 'loading' && (
                        <div className="recording-loading">
                            <div className="rp-spinner"></div>
                            <p>Loading recording...</p>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className="recording-error">
                            <span style={{ fontSize: 52 }}>⚠️</span>
                            <h3>Unable to Load Recording</h3>
                            <p>{errorMsg}</p>
                            <button onClick={loadRecording} className="retry-btn">Try Again</button>
                        </div>
                    )}

                    {phase === 'notFound' && (
                        <div className="recording-error">
                            <span style={{ fontSize: 52 }}>📭</span>
                            <h3>No Recording Found</h3>
                            <p>This session has no recording yet.<br />New sessions are recorded automatically.</p>
                        </div>
                    )}

                    {phase === 'ready' && recording && (
                        <div className="recording-player-wrapper">
                            <div ref={containerRef}></div>
                        </div>
                    )}

                    {/* ── Legacy-session event log (no FullSnapshot → show readable table) ── */}
                    {phase === 'customEvents' && recording && (() => {
                        const fmt = ts => {
                            if (!ts) return '—';
                            const d = new Date(ts);
                            return isNaN(d) ? String(ts) : d.toLocaleTimeString();
                        };
                        const typeColor = t =>
                            t === 'click'        ? '#FF5722' :
                            t === 'input'        ? '#4CAF50' :
                            t === 'change'       ? '#2196F3' :
                            t === 'slide_change' ? '#9C27B0' :
                            t === 'focus'        ? '#FF9800' :
                            t === 'blur'         ? '#607D8B' : '#555';
                        const evts = (recording.events || [])
                            .filter(e => !['mousemove'].includes(e.type))
                            .slice(0, 300);
                        return (
                            <div style={{ padding: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                                        📋 Session Event Log
                                    </div>
                                    <span style={{ color: '#888', fontSize: 13 }}>
                                        {(recording.eventCount || recording.events?.length || 0).toLocaleString()} events recorded
                                    </span>
                                </div>
                                 <div style={{
                                     background: '#1a1a3e', border: '1px solid #E91E63',
                                     borderRadius: 8, padding: '12px 16px', marginBottom: 14
                                 }}>
                                     <p style={{ color: '#E91E63', fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>
                                         ⚠️ No rrweb video replay for this session
                                     </p>
                                     <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 10px' }}>
                                         rrweb was not loaded on this survey session — likely because GHL's strict
                                         Content Security Policy blocked the dynamic script injection.<br />
                                         <strong style={{ color: '#fff' }}>Fix:</strong> Replace the old script URL in your GHL survey
                                         custom code with the <em>bundle URL</em> below (rrweb is pre-inlined, no injection needed):
                                     </p>
                                     <code style={{
                                         display: 'block', background: '#0d0d1a', color: '#4fc3f7',
                                         padding: '8px 12px', borderRadius: 6, fontSize: 12,
                                         wordBreak: 'break-all', userSelect: 'all'
                                     }}>
                                         {window.location.origin}/api/survey-tracking/survey-tracker-bundle.js
                                     </code>
                                     <p style={{ color: '#666', fontSize: 11, margin: '8px 0 0' }}>
                                         💡 After updating the URL, new sessions will show the full video player here.
                                         Old sessions (like this one) will continue to show the interaction log.
                                     </p>
                                 </div>
                                <div style={{ overflowY: 'auto', maxHeight: 460, borderRadius: 8, border: '1px solid #2a2a3e' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#ddd' }}>
                                        <thead>
                                            <tr style={{ background: '#1a1a2e', position: 'sticky', top: 0 }}>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #2a2a3e', width: 90 }}>Time</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #2a2a3e', width: 100 }}>Type</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #2a2a3e' }}>Field / Label</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #2a2a3e' }}>Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {evts.length === 0 ? (
                                                <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#666' }}>No events recorded</td></tr>
                                            ) : evts.map((ev, i) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? '#0d0d1a' : '#12121f', borderBottom: '1px solid #1e1e30' }}>
                                                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11, color: '#888' }}>
                                                        {fmt(ev.ts || ev.timestamp)}
                                                    </td>
                                                    <td style={{ padding: '6px 12px' }}>
                                                        <span style={{
                                                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                                                            background: typeColor(ev.type), color: '#fff', fontWeight: 600
                                                        }}>{ev.type || '?'}</span>
                                                    </td>
                                                    <td style={{ padding: '6px 12px', color: '#ccc' }}>
                                                        {ev.fieldLabel || ev.label || ev.fieldSelector || ev.path || '—'}
                                                        {ev.slideNumber != null && (
                                                            <span style={{ marginLeft: 8, fontSize: 11, color: '#888' }}>
                                                                (Slide {ev.slideNumber})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '6px 12px', color: '#aaa', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {ev.value !== undefined && ev.value !== null
                                                            ? String(ev.value).substring(0, 120)
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* ── Session info bar ── */}
                {(phase === 'ready' || phase === 'customEvents') && recording && (
                    <div className="recording-session-info">
                        {recording.duration        && <span>⏱ {formatDuration(recording.duration)}</span>}
                        {recording.eventCount      && <span>📊 {recording.eventCount.toLocaleString()} events</span>}
                        {recording.recordingStartTime && (
                            <span>🕐 {new Date(recording.recordingStartTime).toLocaleString()}</span>
                        )}
                        {recording.s3Key           && <span>☁️ S3</span>}
                    </div>
                )}
            </div>
        </div>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Active-session helpers ────────────────────────────────────────────────────
const ACTIVE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/** Returns true if a session is currently live (updated within last 5 min, not submitted) */
const isSessionActive = (session) => {
    if (session.submitted) return false;
    if (!session.updatedAt) return false;
    return (Date.now() - new Date(session.updatedAt).getTime()) < ACTIVE_THRESHOLD_MS;
};

/** Pulsing green dot used for the active badge */
const LiveDot = () => (
    <span style={{
        display: 'inline-block',
        width: 9, height: 9,
        borderRadius: '50%',
        background: '#4caf50',
        boxShadow: '0 0 0 0 rgba(76,175,80,0.6)',
        animation: 'livePulse 1.4s infinite',
        flexShrink: 0,
    }} />
);

const SurveyTracking = () => {
    const [campaignType, setCampaignType] = useState('medicare');
    const [sessions, setSessions] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [totalSessions, setTotalSessions] = useState(0);
    const [filterSubmitted, setFilterSubmitted] = useState('all');
    const [filterFlagged, setFilterFlagged] = useState('all');
    const [filterActive, setFilterActive] = useState('all');
    const [filterSurveyName, setFilterSurveyName] = useState('all');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [availableSurveyNames, setAvailableSurveyNames] = useState([]);
    // Recording player
    const [recordingSessionId, setRecordingSessionId] = useState(null);
    // Live ticker (re-renders every 10 s so active badges stay current)
    const [tick, setTick] = useState(0);
    // Silent-refresh indicator
    const [lastRefreshed, setLastRefreshed] = useState(null);   // Date
    const [refreshing, setRefreshing] = useState(false);        // subtle spinner

    // Refs to hold latest filter values inside intervals without stale closures
    const filtersRef = useRef({});

    const limit = 20;

    // Fetch unique survey names
    const fetchSurveyNames = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.get(
                `/api/survey-tracking/admin/campaigns/${campaignType}/survey-names`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAvailableSurveyNames(response.data.data || []);
        } catch (error) {
            console.error('Error fetching survey names:', error);
        }
    };

    // Keep filtersRef in sync so the polling interval can always read the latest values
    useEffect(() => {
        filtersRef.current = {
            campaignType, page, filterSubmitted, filterFlagged,
            filterSurveyName, filterStartDate, filterEndDate, limit
        };
    });

    // Fetch sessions – pass silent=true from the auto-refresh so no "Loading…" flash
    const fetchSessions = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const token = localStorage.getItem('admin_token');
            const f = silent ? filtersRef.current : {
                campaignType, page, filterSubmitted, filterFlagged,
                filterSurveyName, filterStartDate, filterEndDate, limit
            };
            const submitted  = f.filterSubmitted  === 'all' ? undefined : f.filterSubmitted  === 'true';
            const flagged    = f.filterFlagged     === 'all' ? undefined : f.filterFlagged     === 'true';
            const surveyName = f.filterSurveyName  === 'all' ? undefined : f.filterSurveyName;

            const params = new URLSearchParams({
                limit:   f.limit,
                skip:    f.page * f.limit,
                ...(submitted  !== undefined && { submitted }),
                ...(flagged    !== undefined && { flagged }),
                ...(surveyName && { surveyName }),
                ...(f.filterStartDate && { startDate: new Date(f.filterStartDate).toISOString() }),
                ...(f.filterEndDate   && { endDate:   new Date(f.filterEndDate + 'T23:59:59').toISOString() })
            });

            const response = await axios.get(
                `/api/survey-tracking/admin/campaigns/${f.campaignType}/sessions?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSessions(response.data.sessions);
            setTotalSessions(response.data.total);
            if (silent) setLastRefreshed(new Date());
        } catch (error) {
            if (!silent) {
                console.error('Error fetching sessions:', error);
                alert('Failed to fetch sessions');
            }
        } finally {
            if (!silent) setLoading(false);
            else setRefreshing(false);
        }
    };

    // Fetch analytics
    // Uses filtersRef.current so the 10-second auto-refresh interval always picks up
    // the LATEST campaignType even though the interval closure was created at mount.
    const fetchAnalytics = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            // ⭐ FIX: read from filtersRef so stale interval closure gets the right campaign
            const ct = filtersRef.current?.campaignType || campaignType;
            const response = await axios.get(
                `/api/survey-tracking/admin/campaigns/${ct}/analytics`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAnalytics(response.data.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    };

    // Get session details
    const fetchSessionDetails = async (sessionId) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.get(
                `/api/survey-tracking/admin/sessions/${sessionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSelectedSession(response.data.data);
        } catch (error) {
            console.error('Error fetching session details:', error);
        }
    };

    // Flag session
    const flagSession = async (sessionId, flagged, notes) => {
        try {
            const token = localStorage.getItem('admin_token');
            await axios.put(
                `/api/survey-tracking/admin/sessions/${sessionId}/flag`,
                { flagged, adminNotes: notes },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchSessions();
            if (selectedSession?.sessionId === sessionId) {
                fetchSessionDetails(sessionId);
            }
            alert(flagged ? 'Session flagged' : 'Flag removed');
        } catch (error) {
            console.error('Error flagging session:', error);
            alert('Failed to update session');
        }
    };

    // Delete single session
    const deleteSession = async (sessionId) => {
        if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
            return;
        }

        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(
                `/api/survey-tracking/admin/sessions/${sessionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Close modal if the deleted session is currently open
            if (selectedSession?.sessionId === sessionId) {
                setSelectedSession(null);
            }
            
            fetchSessions();
            fetchAnalytics();
            alert('Session deleted successfully');
        } catch (error) {
            console.error('Error deleting session:', error);
            alert('Failed to delete session');
        }
    };

    // Download PDF for session
    const downloadPDF = (sessionId) => {
        const pdfUrl = `/api/public/survey-tracking/sessions/${sessionId}/pdf`;
        window.open(pdfUrl, '_blank');
    };

    // Delete all sessions (with current filters)
    const deleteAllSessions = async () => {
        const params = new URLSearchParams();
        
        if (filterSubmitted !== 'all') {
            params.append('submitted', filterSubmitted);
        }
        if (filterFlagged !== 'all') {
            params.append('flagged', filterFlagged);
        }
        if (filterSurveyName !== 'all') {
            params.append('surveyName', filterSurveyName);
        }
        if (filterStartDate) {
            params.append('startDate', new Date(filterStartDate).toISOString());
        }
        if (filterEndDate) {
            params.append('endDate', new Date(filterEndDate + 'T23:59:59').toISOString());
        }

        const filterDesc = [];
        if (filterSubmitted !== 'all') filterDesc.push(`Submitted: ${filterSubmitted}`);
        if (filterFlagged !== 'all') filterDesc.push(`Flagged: ${filterFlagged}`);
        if (filterSurveyName !== 'all') filterDesc.push(`Survey: ${filterSurveyName}`);
        if (filterStartDate || filterEndDate) filterDesc.push(`Date range`);

        const confirmMessage = filterDesc.length > 0
            ? `Delete all sessions matching these filters?\n\n${filterDesc.join('\n')}\n\nThis will affect ${totalSessions} session(s). This action cannot be undone!`
            : `Delete ALL ${totalSessions} sessions for ${campaignType}? This action cannot be undone!`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const token = localStorage.getItem('admin_token');
            const response = await axios.delete(
                `/api/survey-tracking/admin/campaigns/${campaignType}/sessions?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSelectedSession(null);
            fetchSessions();
            fetchAnalytics();
            alert(`Successfully deleted ${response.data.deletedCount} session(s)`);
        } catch (error) {
            console.error('Error deleting sessions:', error);
            alert('Failed to delete sessions');
        }
    };

    // Initialize and fetch survey names
    useEffect(() => {
        fetchSessions();
        fetchAnalytics();
        fetchSurveyNames();
    }, [campaignType, page, filterSubmitted, filterFlagged, filterSurveyName, filterStartDate, filterEndDate]);

    // ── Silent auto-refresh every 10 s ──────────────────────────────────────────
    // • fetchSessions(true)   → background fetch, no loading spinner, rows stay visible
    // • fetchAnalytics()      → keeps active-count cards fresh
    // • setTick()             → forces re-evaluation of isSessionActive() on all rows
    // The interval stores no stale closures because it reads from filtersRef.current.
    useEffect(() => {
        const id = setInterval(() => {
            setTick(t => t + 1);
            fetchSessions(true);    // silent – no "Loading…" flash
            fetchAnalytics();
        }, 10_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);   // mount once – filtersRef handles the latest state

    const totalPages = Math.ceil(totalSessions / limit);

    /**
     * Render form data organized by the slides where fields were actually interacted with
     * FIX 2026-03-13: Now displays per-field time spent
     */
    const renderFormDataBySlide = (session) => {
        const formData = session.allFormData || {};
        const fieldToSlideMap = session.fieldToSlideMap || {};
        const fieldTotalTime = session.fieldTotalTime || {}; // FIX: Get per-field time data

        console.log('[SurveyTracking] Rendering form data:', {
            hasFormData: Object.keys(formData).length > 0,
            hasFieldToSlideMap: Object.keys(fieldToSlideMap).length > 0,
            hasFieldTotalTime: Object.keys(fieldTotalTime).length > 0,
            formDataKeys: Object.keys(formData),
            mappingKeys: Object.keys(fieldToSlideMap)
        });

        // ⭐ FIX: Check if there's ANY data to display
        const hasAnyData = Object.keys(formData).length > 0 || Object.keys(fieldToSlideMap).length > 0;

        if (!hasAnyData) {
            return (
                <div className="form-data">
                    <p style={{ fontStyle: 'italic', color: '#999' }}>
                        No form data captured yet for this session
                    </p>
                </div>
            );
        }

        // If we have form data but no mapping, show simple list
        if (!fieldToSlideMap || Object.keys(fieldToSlideMap).length === 0) {
            return (
                <div className="form-data">
                    <p style={{ fontStyle: 'italic', color: '#666', marginBottom: '10px' }}>
                        ℹ️ Field-to-slide mapping not available for this session (older session or tracking not enabled)
                    </p>
                    {Object.entries(formData).map(([key, value]) => (
                        <div key={key} className="form-field">
                            <strong>{key}:</strong>
                            <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                        </div>
                    ))}
                </div>
            );
        }

        // Group fields by slide number
        const slideGroups = {};

        Object.entries(formData).forEach(([fieldSelector, fieldValue]) => {
            const fieldMapping = fieldToSlideMap[fieldSelector];

            if (fieldMapping) {
                const slideNum = fieldMapping.slideNumber;
                const fieldLabel = fieldMapping.fieldLabel || fieldSelector;

                if (!slideGroups[slideNum]) {
                    slideGroups[slideNum] = [];
                }

                slideGroups[slideNum].push({
                    fieldSelector,
                    fieldLabel,
                    fieldValue,
                    lastInteraction: fieldMapping.lastInteraction,
                    timeSpent: fieldTotalTime[fieldSelector] || 0 // FIX: Add time spent
                });
            } else {
                // Field in formData but not in mapping (shouldn't happen, but handle it)
                if (!slideGroups['unknown']) {
                    slideGroups['unknown'] = [];
                }
                slideGroups['unknown'].push({
                    fieldSelector,
                    fieldLabel: fieldSelector,
                    fieldValue,
                    lastInteraction: null,
                    timeSpent: fieldTotalTime[fieldSelector] || 0 // FIX: Add time spent
                });
            }
        });

        // Sort slide numbers
        const sortedSlideNumbers = Object.keys(slideGroups).sort((a, b) => {
            if (a === 'unknown') return 1;
            if (b === 'unknown') return -1;
            return parseInt(a) - parseInt(b);
        });

        return (
            <div className="form-data-by-slide">
                {sortedSlideNumbers.map(slideNum => (
                    <details key={slideNum} className="slide-group" open={slideNum !== 'unknown'}>
                        <summary className="slide-group-header">
                            <strong>
                                {slideNum === 'unknown' ? '❓ Unknown Slide' : `📄 Slide ${slideNum}`}
                            </strong>
                            <span className="field-count">({slideGroups[slideNum].length} field{slideGroups[slideNum].length !== 1 ? 's' : ''})</span>
                        </summary>
                        <div className="slide-fields">
                            {slideGroups[slideNum].map((field, idx) => {
                                // Check if this is a signature field (base64 image data)
                                const isSignatureImage = String(field.fieldValue).startsWith('data:image/');
                                const isSignatureText = field.fieldSelector.includes('signature-') || 
                                                       String(field.fieldValue).includes('Signature Present') ||
                                                       String(field.fieldValue).includes('✅ Signature');
                                
                                return (
                                    <div key={idx} className="form-field">
                                        <div className="field-label-section">
                                            <strong>{field.fieldLabel}</strong>
                                            <div className="field-meta-row">
                                                {field.lastInteraction && (
                                                    <span className="field-meta">
                                                        Last interaction: {new Date(field.lastInteraction).toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                            {field.timeSpent > 0 && (
                                                <div className="field-meta-row" style={{ marginTop: '4px' }}>
                                                    <span className="field-meta field-time">
                                                        ⏱️ Time spent: {formatTime(field.timeSpent)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="field-value">
                                            {isSignatureImage ? (
                                                <div style={{ marginTop: '10px' }}>
                                                    <div style={{ 
                                                        background: '#e8f5e9', 
                                                        color: '#2e7d32',
                                                        fontWeight: '600',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        marginBottom: '10px',
                                                        display: 'inline-block'
                                                    }}>
                                                        ✍️ Signature Captured ({Math.round(field.fieldValue.length / 1024)}KB)
                                                    </div>
                                                    <div style={{
                                                        border: '2px solid #4CAF50',
                                                        borderRadius: '8px',
                                                        padding: '10px',
                                                        background: '#f5f5f5',
                                                        display: 'inline-block'
                                                    }}>
                                                        <img 
                                                            src={field.fieldValue} 
                                                            alt="Signature" 
                                                            style={{ 
                                                                maxWidth: '400px', 
                                                                height: 'auto',
                                                                display: 'block',
                                                                background: 'white'
                                                            }} 
                                                        />
                                                    </div>
                                                </div>
                                            ) : isSignatureText ? (
                                                <div style={{ 
                                                    background: '#fff3e0', 
                                                    color: '#e65100',
                                                    fontWeight: '600',
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    display: 'inline-block'
                                                }}>
                                                    ⚠️ Signature field detected but image not captured (legacy data)
                                                </div>
                                            ) : typeof field.fieldValue === 'object' ? (
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                                    {JSON.stringify(field.fieldValue, null, 2)}
                                                </pre>
                                            ) : (
                                                String(field.fieldValue)
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </details>
                ))}
            </div>
        );
    };

    return (
        <div className="survey-tracking-container">
            <h1>Survey Tracking & Analytics</h1>

            {/* Campaign Selection */}
            <div className="campaign-selector">
                <label>Campaign Type:</label>
                <select value={campaignType} onChange={(e) => { setCampaignType(e.target.value); setPage(0); }}>
                    <option value="medicare">Medicare</option>
                    <option value="aca">ACA</option>
                </select>
            </div>

            {/* Analytics Cards */}
            {analytics && (
                <div className="analytics-cards">
                    <div className="card">
                        <h3>Total Sessions</h3>
                        <p className="big-number">{analytics.totalSessions}</p>
                    </div>
                    <div className="card">
                        <h3>Submitted</h3>
                        <p className="big-number">{analytics.submittedSessions}</p>
                    </div>
                    <div className="card">
                        <h3>Conversion Rate</h3>
                        <p className="big-number">{analytics.conversionRate}%</p>
                    </div>
                    <div className="card">
                        <h3>Avg Duration</h3>
                        <p className="big-number">{formatTime(analytics.avgSessionDuration)}</p>
                    </div>
                    <div className="card">
                        <h3>Avg Fields Interacted</h3>
                        <p className="big-number">{analytics.avgFieldsInteracted}</p>
                    </div>
                    <div className="card">
                        <h3>Avg Slides Viewed</h3>
                        <p className="big-number">{analytics.avgSlidesViewed}</p>
                    </div>
                    <div className="card flagged">
                        <h3>Flagged Sessions</h3>
                        <p className="big-number">{analytics.flaggedCount}</p>
                    </div>
                    {/* ── Active / Live Sessions card ── */}
                    <div className="card" style={{
                        border: '2px solid #4caf50',
                        background: 'linear-gradient(135deg,#e8f5e9 0%,#f1f8e9 100%)',
                        position: 'relative', overflow: 'hidden'
                    }}>
                        {/* animated green glow strip at top */}
                        <div style={{
                            position:'absolute', top:0, left:0, right:0, height:3,
                            background:'#4caf50',
                            animation:'livePulse 1.4s infinite'
                        }} />
                        <h3 style={{ color:'#2e7d32', display:'flex', alignItems:'center', gap:6 }}>
                            <LiveDot /> Live Now
                        </h3>
                        <p className="big-number" style={{ color:'#2e7d32' }}>
                            {analytics.activeSessions ?? 0}
                        </p>
                        <p style={{ fontSize:11, color:'#66bb6a', margin:'4px 0 0', fontStyle:'italic' }}>
                            active in last 5 min
                        </p>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="filters">
                <label>
                    Survey Name:
                    <select value={filterSurveyName} onChange={(e) => { setFilterSurveyName(e.target.value); setPage(0); }}>
                        <option value="all">All Surveys</option>
                        {availableSurveyNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Start Date:
                    <input
                        type="date"
                        value={filterStartDate}
                        onChange={(e) => { setFilterStartDate(e.target.value); setPage(0); }}
                    />
                </label>
                <label>
                    End Date:
                    <input
                        type="date"
                        value={filterEndDate}
                        onChange={(e) => { setFilterEndDate(e.target.value); setPage(0); }}
                    />
                </label>
                <label>
                    Submitted:
                    <select value={filterSubmitted} onChange={(e) => { setFilterSubmitted(e.target.value); setPage(0); }}>
                        <option value="all">All</option>
                        <option value="true">Submitted Only</option>
                        <option value="false">Not Submitted</option>
                    </select>
                </label>
                <label>
                    Status:
                    <select value={filterFlagged} onChange={(e) => { setFilterFlagged(e.target.value); setPage(0); }}>
                        <option value="all">All</option>
                        <option value="true">Flagged</option>
                        <option value="false">Not Flagged</option>
                    </select>
                </label>
                {/* ── Active / Live filter ── */}
                <label>
                    Live:
                    <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setPage(0); }}
                        style={{ borderColor: filterActive === 'active' ? '#4caf50' : undefined }}>
                        <option value="all">All</option>
                        <option value="active">🟢 Active Only</option>
                    </select>
                </label>
            </div>

            {/* Sessions List */}
            <div className="sessions-list">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <h2 style={{ margin: 0 }}>Sessions</h2>
                        {/* ── Silent-refresh indicator ── */}
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            fontSize: 12, color: '#888',
                            background: '#f0f0f0', borderRadius: 20,
                            padding: '3px 10px', userSelect: 'none',
                        }}>
                            {refreshing ? (
                                <>
                                    <span style={{
                                        width: 9, height: 9, borderRadius: '50%',
                                        border: '2px solid #4caf50',
                                        borderTopColor: 'transparent',
                                        display: 'inline-block',
                                        animation: 'spin 0.7s linear infinite',
                                    }} />
                                    <span style={{ color: '#4caf50' }}>Refreshing…</span>
                                </>
                            ) : (
                                <>
                                    <span style={{ color: '#4caf50' }}>●</span>
                                    <span>
                                        {lastRefreshed
                                            ? `Updated ${Math.round((Date.now() - lastRefreshed.getTime()) / 1000)}s ago`
                                            : 'Auto-refresh every 10s'}
                                    </span>
                                </>
                            )}
                        </span>
                    </div>
                    {totalSessions > 0 && (
                        <button 
                            className="delete-all-btn" 
                            onClick={deleteAllSessions}
                            style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '14px'
                            }}
                        >
                            🗑️ Delete All Sessions ({totalSessions})
                        </button>
                    )}
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Survey Name</th>
                            <th>Lead Name</th>
                            <th>Email</th>
                            <th>Started</th>
                            <th>Duration</th>
                            <th>Fields</th>
                            <th>Submitted</th>
                            <th>Flagged</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="10">Loading...</td></tr>
                        ) : sessions.length === 0 ? (
                            <tr><td colSpan="10">No sessions found</td></tr>
                        ) : (
                            sessions
                              .filter(s => filterActive === 'all' || isSessionActive(s))
                              .map(session => {
                                const liveNow = isSessionActive(session);
                                return (
                                <tr key={session.sessionId || session._id}
                                    className={session.flagged ? 'flagged-row' : ''}
                                    style={liveNow ? { background: '#f0fdf4', borderLeft: '3px solid #4caf50' } : {}}>
                                    <td className="survey-name-cell">
                                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                            {liveNow && <LiveDot />}
                                            {liveNow && <span style={{ fontSize:11, color:'#2e7d32', fontWeight:700, letterSpacing:'0.05em' }}>LIVE</span>}
                                            {session.surveyName || '-'}
                                        </div>
                                    </td>
                                    <td>{session.leadFirstName} {session.leadLastName}</td>
                                    <td>{session.leadEmail || '-'}</td>
                                    <td>{new Date(session.startTime).toLocaleDateString()} {new Date(session.startTime).toLocaleTimeString()}</td>
                                    <td>{formatTime(session.totalDuration || 0)}</td>
                                    <td>{session.totalFieldsInteracted}</td>
                                    <td><span className={`badge ${session.submitted ? 'submitted' : 'pending'}`}>{session.submitted ? 'Yes' : 'No'}</span></td>
                                    <td><span className={`badge ${session.flagged ? 'flagged' : 'normal'}`}>{session.flagged ? 'Yes' : 'No'}</span></td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            <button 
                                                onClick={() => fetchSessionDetails(session.sessionId)}
                                                style={{
                                                    background: '#2196F3',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                👁️ View
                                            </button>
                                            <button 
                                                onClick={() => downloadPDF(session.sessionId)}
                                                style={{
                                                    background: '#4CAF50',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                                title="Download PDF Report"
                                            >
                                                📄 PDF
                                            </button>
                                            <button 
                                                onClick={() => setRecordingSessionId(session.sessionId)}
                                                style={{
                                                    background: '#E91E63',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                                title="View Session Recording"
                                            >
                                                🎥 Rec
                                            </button>
                                            <button 
                                                onClick={() => deleteSession(session.sessionId)}
                                                style={{
                                                    background: '#f44336',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                                title="Delete this session"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                              })
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                <div className="pagination">
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span>{page + 1} / {totalPages || 1}</span>
                    <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            </div>

            {/* Session Details Modal */}
            {selectedSession && (
                <div className="modal-overlay" onClick={() => setSelectedSession(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedSession(null)}>✕</button>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0 }}>Session Details</h2>
                            <button
                                onClick={() => downloadPDF(selectedSession.sessionId)}
                                style={{
                                    background: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                📄 Download PDF Report
                            </button>
                        </div>

                        <div className="session-details">
                            <div className="detail-section">
                                <h3>Lead Information</h3>
                                <p><strong>Name:</strong> {selectedSession.leadFirstName} {selectedSession.leadLastName}</p>
                                <p><strong>Email:</strong> {selectedSession.leadEmail}</p>
                                <p><strong>Phone:</strong> {selectedSession.leadPhone}</p>
                            </div>

                            <div className="detail-section">
                                <h3>Session Metadata</h3>
                                <p><strong>Session ID:</strong> {selectedSession.sessionId}</p>
                                <p><strong>Campaign:</strong> {selectedSession.campaignType}</p>
                                <p><strong>Survey Name:</strong> {selectedSession.surveyName || 'N/A'}</p>
                                <p><strong>Started:</strong> {new Date(selectedSession.startTime).toLocaleString()}</p>
                                <p><strong>Submitted:</strong> {selectedSession.submitted ? new Date(selectedSession.submittedAt).toLocaleString() : 'Not submitted'}</p>
                                <p><strong>Total Duration:</strong> {formatTime(selectedSession.totalDuration || 0)}</p>
                                <p><strong>User Agent:</strong> {selectedSession.userAgent}</p>
                                <p><strong>IP Address:</strong> {selectedSession.ipAddress}</p>
                            </div>

                            <div className="detail-section">
                                <h3>Interaction Stats</h3>
                                <p><strong>Slides Viewed:</strong> {selectedSession.slidesViewed?.length}</p>
                                <p><strong>Total Fields Interacted:</strong> {selectedSession.totalFieldsInteracted}</p>
                                <p><strong>Total Interaction Events:</strong> {selectedSession.totalInteractionEvents}</p>
                                <p><strong>Avg Time Per Slide:</strong> {formatTime(selectedSession.avgTimePerSlide || 0)}</p>
                            </div>

                            {/* Device Fingerprint */}
                            {selectedSession.deviceFingerprint && Object.keys(selectedSession.deviceFingerprint).length > 0 && (
                                <div className="detail-section">
                                    <h3>🔐 Device & Browser Fingerprint</h3>

                                    {selectedSession.deviceFingerprint.hash && (
                                        <p><strong>Fingerprint Hash:</strong> <code>{selectedSession.deviceFingerprint.hash}</code></p>
                                    )}

                                    {selectedSession.deviceFingerprint.fingerprintedAt && (
                                        <p><strong>Generated:</strong> {new Date(selectedSession.deviceFingerprint.fingerprintedAt).toLocaleString()}</p>
                                    )}

                                    {/* Screen Info */}
                                    {selectedSession.deviceFingerprint.screen && (
                                        <details className="fingerprint-section" open>
                                            <summary><strong>📺 Screen</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Resolution: {selectedSession.deviceFingerprint.screen.width}x{selectedSession.deviceFingerprint.screen.height}</p>
                                                <p>Available: {selectedSession.deviceFingerprint.screen.availWidth}x{selectedSession.deviceFingerprint.screen.availHeight}</p>
                                                <p>Color Depth: {selectedSession.deviceFingerprint.screen.colorDepth}-bit</p>
                                                <p>Pixel Depth: {selectedSession.deviceFingerprint.screen.pixelDepth}-bit</p>
                                                <p>Device Pixel Ratio: {selectedSession.deviceFingerprint.screen.devicePixelRatio}x</p>
                                                <p>Orientation: {selectedSession.deviceFingerprint.screen.orientation}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Viewport Info */}
                                    {selectedSession.deviceFingerprint.viewport && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🖥️ Viewport</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Inner: {selectedSession.deviceFingerprint.viewport.innerWidth}x{selectedSession.deviceFingerprint.viewport.innerHeight}</p>
                                                <p>Outer: {selectedSession.deviceFingerprint.viewport.outerWidth}x{selectedSession.deviceFingerprint.viewport.outerHeight}</p>
                                                <p>Position: ({selectedSession.deviceFingerprint.viewport.screenX}, {selectedSession.deviceFingerprint.viewport.screenY})</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Browser Info */}
                                    {selectedSession.deviceFingerprint.browser && (
                                        <details className="fingerprint-section" open>
                                            <summary><strong>🌐 Browser</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Platform: {selectedSession.deviceFingerprint.browser.platform}</p>
                                                <p>Language: {selectedSession.deviceFingerprint.browser.language}</p>
                                                <p>Languages: {selectedSession.deviceFingerprint.browser.languages?.join(', ')}</p>
                                                <p>CPU Cores: {selectedSession.deviceFingerprint.browser.hardwareConcurrency}</p>
                                                <p>Memory: {selectedSession.deviceFingerprint.browser.deviceMemory} GB</p>
                                                <p>Touch Points: {selectedSession.deviceFingerprint.browser.maxTouchPoints}</p>
                                                <p>Vendor: {selectedSession.deviceFingerprint.browser.vendor}</p>
                                                <p>Vendor Sub: {selectedSession.deviceFingerprint.browser.vendorSub}</p>
                                                <p>Product Sub: {selectedSession.deviceFingerprint.browser.productSub}</p>
                                                <p>Build ID: {selectedSession.deviceFingerprint.browser.buildID}</p>
                                                <p>Cookies: {selectedSession.deviceFingerprint.browser.cookieEnabled ? 'Enabled' : 'Disabled'}</p>
                                                <p>Online: {selectedSession.deviceFingerprint.browser.onLine ? 'Yes' : 'No'}</p>
                                                <p>Do Not Track: {selectedSession.deviceFingerprint.browser.doNotTrack || 'Not set'}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Touch Support */}
                                    {selectedSession.deviceFingerprint.touchSupport && (
                                        <details className="fingerprint-section">
                                            <summary><strong>👆 Touch Support</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Max Touch Points: {selectedSession.deviceFingerprint.touchSupport.maxTouchPoints}</p>
                                                <p>Touch Event Support: {selectedSession.deviceFingerprint.touchSupport.touchEvent ? 'Yes' : 'No'}</p>
                                                <p>Touch Points API: {selectedSession.deviceFingerprint.touchSupport.touchPoints ? 'Yes' : 'No'}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* WebGL Info */}
                                    {selectedSession.deviceFingerprint.webgl && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🎮 GPU (WebGL)</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Vendor: {selectedSession.deviceFingerprint.webgl.vendor}</p>
                                                <p>Renderer: {selectedSession.deviceFingerprint.webgl.renderer}</p>
                                                <p>Version: {selectedSession.deviceFingerprint.webgl.version}</p>
                                                <p>Shading Language: {selectedSession.deviceFingerprint.webgl.shadingLanguageVersion}</p>
                                                <p>Max Texture Size: {selectedSession.deviceFingerprint.webgl.maxTextureSize}</p>
                                                <p>Max Renderbuffer Size: {selectedSession.deviceFingerprint.webgl.maxRenderbufferSize}</p>
                                                <p>Max Viewport: {selectedSession.deviceFingerprint.webgl.maxViewportDims?.[0]}x{selectedSession.deviceFingerprint.webgl.maxViewportDims?.[1]}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Canvas Fingerprint */}
                                    {selectedSession.deviceFingerprint.canvas && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🎨 Canvas Fingerprint</strong></summary>
                                            <div className="fingerprint-details">
                                                <img src={selectedSession.deviceFingerprint.canvas} alt="Canvas Fingerprint" style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }} />
                                            </div>
                                        </details>
                                    )}

                                    {/* Audio Fingerprint */}
                                    {selectedSession.deviceFingerprint.audio && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🔊 Audio Context</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Sample Rate: {selectedSession.deviceFingerprint.audio} Hz</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Connection Info */}
                                    {selectedSession.deviceFingerprint.connection && (
                                        <details className="fingerprint-section">
                                            <summary><strong>📶 Network Connection</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Type: {selectedSession.deviceFingerprint.connection.effectiveType}</p>
                                                <p>Connection Type: {selectedSession.deviceFingerprint.connection.type}</p>
                                                <p>Downlink: {selectedSession.deviceFingerprint.connection.downlink} Mbps</p>
                                                <p>Latency (RTT): {selectedSession.deviceFingerprint.connection.rtt} ms</p>
                                                <p>Save Data: {selectedSession.deviceFingerprint.connection.saveData ? 'Enabled' : 'Disabled'}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Battery Info */}
                                    {selectedSession.deviceFingerprint.battery && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🔋 Battery</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Level: {Math.round(selectedSession.deviceFingerprint.battery.level * 100)}%</p>
                                                <p>Charging: {selectedSession.deviceFingerprint.battery.charging ? 'Yes' : 'No'}</p>
                                                <p>Charging Time: {selectedSession.deviceFingerprint.battery.chargingTime ? `${selectedSession.deviceFingerprint.battery.chargingTime}s` : 'N/A'}</p>
                                                <p>Discharging Time: {selectedSession.deviceFingerprint.battery.dischargingTime ? `${selectedSession.deviceFingerprint.battery.dischargingTime}s` : 'N/A'}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Timezone */}
                                    {selectedSession.deviceFingerprint.timezone && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🌍 Timezone</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Timezone: {selectedSession.deviceFingerprint.timezone.timezone}</p>
                                                <p>Offset: UTC{selectedSession.deviceFingerprint.timezone.offset > 0 ? '-' : '+'}{Math.abs(selectedSession.deviceFingerprint.timezone.offset / 60)}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Media Devices */}
                                    {selectedSession.deviceFingerprint.mediaDevices && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🎤 Media Devices</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>Audio Input: {selectedSession.deviceFingerprint.mediaDevices.audioInput}</p>
                                                <p>Audio Output: {selectedSession.deviceFingerprint.mediaDevices.audioOutput}</p>
                                                <p>Video Input: {selectedSession.deviceFingerprint.mediaDevices.videoInput}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Permissions */}
                                    {selectedSession.deviceFingerprint.permissions && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🔒 Permissions</strong></summary>
                                            <div className="fingerprint-details">
                                                {Object.entries(selectedSession.deviceFingerprint.permissions).map(([key, value]) => (
                                                    <p key={key}>{key}: {value}</p>
                                                ))}
                                            </div>
                                        </details>
                                    )}

                                    {/* Plugins */}
                                    {selectedSession.deviceFingerprint.plugins && selectedSession.deviceFingerprint.plugins.length > 0 && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🔌 Browser Plugins ({selectedSession.deviceFingerprint.plugins.length})</strong></summary>
                                            <div className="fingerprint-details">
                                                {selectedSession.deviceFingerprint.plugins.map((plugin, idx) => (
                                                    <p key={idx}>
                                                        <strong>{plugin.name}</strong>
                                                        {plugin.description && ` - ${plugin.description}`}
                                                        {plugin.filename && ` (${plugin.filename})`}
                                                    </p>
                                                ))}
                                            </div>
                                        </details>
                                    )}

                                    {/* Fonts */}
                                    {selectedSession.deviceFingerprint.fonts && selectedSession.deviceFingerprint.fonts.length > 0 && (
                                        <details className="fingerprint-section">
                                            <summary><strong>🔤 Installed Fonts ({selectedSession.deviceFingerprint.fonts.length})</strong></summary>
                                            <div className="fingerprint-details">
                                                <p>{selectedSession.deviceFingerprint.fonts.join(', ')}</p>
                                            </div>
                                        </details>
                                    )}

                                    {/* Features */}
                                    {selectedSession.deviceFingerprint.features && (
                                        <details className="fingerprint-section">
                                            <summary><strong>⚙️ Browser Features</strong></summary>
                                            <div className="fingerprint-details">
                                                {Object.entries(selectedSession.deviceFingerprint.features).map(([key, value]) => (
                                                    <p key={key}>{key}: {value ? '✓' : '✗'}</p>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Slide Breakdown */}
                            {selectedSession.slidesViewed && selectedSession.slidesViewed.length > 0 && (
                                <div className="detail-section">
                                    <h3>Slide Breakdown</h3>
                                    <table className="slide-table">
                                        <thead>
                                            <tr>
                                                <th>Slide #</th>
                                                <th>Time Spent</th>
                                                <th>Focus Count</th>
                                                <th>Click Count</th>
                                                <th>Interactions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSession.slidesViewed.map((slide, idx) => (
                                                <tr key={idx}>
                                                    <td>{slide.slideNumber}</td>
                                                    <td>{formatTime(slide.timeSpent || 0)}</td>
                                                    <td>{slide.focusCount || 0}</td>
                                                    <td>{slide.clickCount || 0}</td>
                                                    <td>{slide.interactions?.length || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Form Data Organized by Slides */}
                            {selectedSession.allFormData && Object.keys(selectedSession.allFormData).length > 0 && (
                                <div className="detail-section">
                                    <h3>📋 Complete Form Data - Organized by Slide</h3>
                                    {renderFormDataBySlide(selectedSession)}
                                </div>
                            )}

                            {/* Admin Review */}
                            <div className="detail-section">
                                <h3>Admin Review</h3>
                                <div className="admin-review">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={selectedSession.flagged || false}
                                            onChange={(e) => {
                                                const notes = prompt('Add notes (optional):', selectedSession.adminNotes || '');
                                                if (notes !== null) {
                                                    flagSession(selectedSession.sessionId, e.target.checked, notes);
                                                }
                                            }}
                                        />
                                        Flag this session for review
                                    </label>
                                    {selectedSession.adminNotes && (
                                        <p><strong>Notes:</strong> {selectedSession.adminNotes}</p>
                                    )}
                                    {selectedSession.reviewedBy && (
                                        <p><strong>Reviewed by:</strong> {selectedSession.reviewedBy}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Session Recording Player Modal ── */}
            {recordingSessionId && (
                <RecordingPlayer
                    sessionId={recordingSessionId}
                    onClose={() => setRecordingSessionId(null)}
                />
            )}
        </div>
    );
};

export default SurveyTracking;
